import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, RefreshCw, LogOut, UserCheck, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth, isPlanActive } from "../contexts/AuthContext";
import PaymentModal from "../components/PaymentModal";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcessoBloqueado() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && isPlanActive(user)) {
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [checking, setChecking]       = useState(false);
  const [emailInput, setEmailInput]   = useState("");
  const [showEmailBox, setShowEmailBox] = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [visible, setVisible]         = useState(false);

  useEffect(() => { setTimeout(() => setVisible(true), 50); }, []);

  const planExpirado = user?.plano === "expirado" || user?.plano_status === "expirado" || user?.plano_status === "inativo";
  const jaTemPlano = user && user.plano && user.plano !== "nenhum" && user.plano !== "trial" && !planExpirado;

  const handleVerificarCompra = async () => {
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/mercadopago/verificar-compra`);
      if (data.activated && !data.already_active) {
        const updated = await refreshUser();
        if (isPlanActive(updated)) {
          toast.success("Pagamento encontrado! Acesso liberado.");
          navigate("/app");
          return;
        }
      }
      const updated = await refreshUser();
      if (isPlanActive(updated)) {
        navigate("/app");
      } else {
        toast.info("Pagamento ainda não confirmado. Se acabou de pagar, aguarde 1 minuto e tente novamente.");
      }
    } catch {
      toast.info("Pagamento ainda não confirmado. Se acabou de pagar, aguarde 1 minuto e tente novamente.");
    } finally {
      setChecking(false);
    }
  };

  const handleVerificarEmail = async (e) => {
    e.preventDefault();
    if (!emailInput) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/auth/sync-plan`, {}, {
        headers: { "X-Check-Email": emailInput },
      });
      if (isPlanActive(data)) {
        toast.success("Plano encontrado! Faça login para acessar.");
        navigate("/login");
      } else {
        toast.info("Não encontramos um plano ativo para este e-mail. Verifique se usou o mesmo e-mail da compra.");
      }
    } catch {
      toast.info("Use o e-mail cadastrado na compra para fazer login.");
      navigate("/login");
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleForceAdmin = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/auth/force-admin`);
      const updated = await refreshUser();
      if (isPlanActive(updated)) {
        toast.success("Acesso liberado!");
        navigate("/app");
      } else {
        toast.error("Ainda bloqueado. Veja o debug abaixo.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao forçar acesso.");
    } finally {
      setChecking(false);
    }
  };

  const planoBadge = user?.plano === "trial" ? "Trial" : user?.plano || "Sem plano";
  const primeiroNome = user?.name?.split(" ")[0];

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 text-white relative overflow-hidden"
      style={{ background: "#0f1117" }}
    >
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: .3; transform: scale(1); }
          50%       { opacity: .5; transform: scale(1.1); }
        }
        .fade-in-up { animation: fadeInUp 0.5s cubic-bezier(.22,.68,0,1.2) both; }
        .glow-pulse  { animation: pulseGlow 3s ease-in-out infinite; }
      `}</style>

      {/* Background glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="glow-pulse absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/8 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-blue-600/6 blur-[120px]" />
        <div className="fixed inset-0 grid-pattern opacity-[.15]" />
      </div>

      {/* ── Card ── */}
      <div
        className="relative z-10 w-full max-w-md rounded-2xl overflow-hidden fade-in-up"
        style={{
          background: "#0D1B2E",
          border: "1px solid rgba(255,255,255,.08)",
          boxShadow: "0 24px 80px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.04)",
        }}
      >
        {/* Card header — logo */}
        <div
          className="flex justify-center pt-6 pb-5"
          style={{ borderBottom: "1px solid rgba(255,255,255,.06)" }}
        >
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/monyx-bot-hero.png" alt="Monyxx" className="h-9 w-9 object-contain" />
            <div className="font-display text-xl font-extrabold">
              <span className="text-emerald-400">M</span>
              <span className="text-white">ony</span>
              <span className="text-blue-400">xx</span>
              <span className="ml-2 text-[9px] tracking-[0.3em] font-bold text-emerald-400/60">FINANCE</span>
            </div>
          </Link>
        </div>

        {/* Card body */}
        <div className="px-7 py-7">
          {/* Lock icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl scale-[1.8] glow-pulse" />
              <div
                className="relative w-20 h-20 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #0d1f14 0%, #111827 100%)",
                  border: "1.5px solid rgba(52,211,153,.25)",
                  boxShadow: "0 0 32px rgba(52,211,153,.1)",
                }}
              >
                <Lock className="w-9 h-9 text-emerald-400" strokeWidth={1.5} />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-center font-display text-3xl font-bold text-white mb-2 tracking-tight">
            Acesso Bloqueado
          </h1>

          {/* Subtitle */}
          <p className="text-center text-slate-400 text-sm leading-relaxed mb-6">
            {primeiroNome
              ? `Olá, ${primeiroNome}. Para continuar é necessário ter um plano ativo.`
              : "Para acessar o Monyxx Finance é necessário ter um plano ativo."}
          </p>

          {/* User info */}
          {user && (
            <div
              className="mb-5 p-3.5 rounded-xl flex items-center gap-3"
              style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.07)" }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold text-emerald-400 text-sm"
                style={{ background: "rgba(52,211,153,.1)", border: "1px solid rgba(52,211,153,.2)" }}
              >
                {user.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-medium truncate">{user.name}</p>
                <p className="text-slate-500 text-xs truncate">{user.email}</p>
              </div>
              <span className="shrink-0 text-[11px] px-2.5 py-1 rounded-full text-slate-400"
                style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)" }}>
                {planoBadge}
              </span>
            </div>
          )}

          {/* CASO 1: logado sem plano */}
          {user && !jaTemPlano && (
            <div className="space-y-3">
              <button onClick={() => setPaymentOpen(true)}
                className="w-full py-3.5 rounded-xl font-bold text-base text-slate-950 transition-all active:scale-[.98]"
                style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)", boxShadow: "0 4px 20px rgba(34,197,94,.3)" }}>
                Ver Planos
              </button>
              <button onClick={handleVerificarCompra} disabled={checking}
                className="w-full py-3.5 rounded-xl font-semibold text-base text-emerald-400 flex items-center justify-center gap-2 transition-all hover:bg-emerald-500/10 disabled:opacity-60"
                style={{ border: "1.5px solid rgba(52,211,153,.35)" }}>
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {checking ? "Verificando..." : "Já comprei"}
              </button>
            </div>
          )}

          {/* CASO 2: tem plano mas bloqueado */}
          {user && jaTemPlano && (
            <div className="space-y-3">
              <p className="text-center text-slate-500 text-sm">Seu plano foi detectado mas precisa ser sincronizado.</p>
              <button onClick={handleVerificarCompra} disabled={checking}
                className="w-full py-3.5 rounded-xl font-bold text-base text-slate-950 flex items-center justify-center gap-2 transition-all active:scale-[.98] disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)", boxShadow: "0 4px 20px rgba(34,197,94,.3)" }}>
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {checking ? "Sincronizando..." : "Sincronizar e acessar"}
              </button>
            </div>
          )}

          {/* CASO 3: não logado */}
          {!user && (
            <div className="space-y-3">
              <button onClick={() => setPaymentOpen(true)}
                className="w-full py-3.5 rounded-xl font-bold text-base text-slate-950 transition-all active:scale-[.98]"
                style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)", boxShadow: "0 4px 20px rgba(34,197,94,.3)" }}>
                Ver Planos
              </button>
              <button onClick={() => navigate("/login")}
                className="w-full py-3.5 rounded-xl font-semibold text-base text-slate-300 flex items-center justify-center gap-2 transition-all hover:bg-white/5"
                style={{ border: "1px solid rgba(255,255,255,.1)" }}>
                <UserCheck className="w-4 h-4" /> Já sou cliente — fazer login
              </button>
              {!showEmailBox ? (
                <button onClick={() => setShowEmailBox(true)}
                  className="w-full text-sm text-slate-500 hover:text-emerald-400 transition-colors py-1.5">
                  Já comprei mas não tenho conta cadastrada
                </button>
              ) : (
                <form onSubmit={handleVerificarEmail} className="space-y-2 pt-1">
                  <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="E-mail usado na compra" required
                    className="w-full rounded-xl px-4 py-3 text-white text-sm focus:outline-none transition-colors"
                    style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)" }} />
                  <button type="submit" disabled={syncing}
                    className="w-full py-3 rounded-xl text-sm font-semibold text-slate-300 flex items-center justify-center gap-2 hover:bg-white/5 transition-all"
                    style={{ border: "1px solid rgba(255,255,255,.1)" }}>
                    {syncing && <Loader2 className="w-4 h-4 animate-spin" />}
                    Verificar e-mail
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Links discretos */}
          <div className="flex flex-col items-center gap-1 mt-6">
            {user && (
              <button onClick={() => navigate("/login")}
                className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-sm py-1.5 transition-colors">
                <UserCheck className="w-3.5 h-3.5" /> Entrar com outra conta
              </button>
            )}
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-sm py-1.5 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Sair da conta
            </button>
          </div>
        </div>

        {/* Card footer — admin */}
        {user && (
          <div
            className="px-7 pb-5 flex flex-col items-center gap-2"
            style={{ borderTop: "1px solid rgba(255,255,255,.05)" }}
          >
            <button onClick={handleForceAdmin} disabled={checking}
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-yellow-500/80 transition-colors py-2 disabled:opacity-40 mt-1">
              {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : "🔧"}
              Sou o administrador — liberar acesso
            </button>
            <p className="text-[10px] text-slate-700">{user.email} · {planoBadge} · {user.role}</p>
          </div>
        )}
      </div>

      <PaymentModal
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        userEmail={user?.email}
        userName={user?.name}
      />
    </div>
  );
}
